import { Box, CircularProgress, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { persistSession } from "../../api/AuthSession";
import { userStore } from "../../stores/sharedStores";
import { useNotification } from "../../utils/NotificationsContext";
import { useActiveWorkspace } from "../../utils/WorkspaceContext";
import CustomTypography from "../components/common/form/CustomTypography";
import { getIdentityErrorMessage } from "../components/login/loginErrors";

const processedGoogleCodes = new Set<string>();

const GoogleSuccessPopup = () => {
  const theme = useTheme();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeWorkspace = useActiveWorkspace();
  const [statusMessage, setStatusMessage] = useState(
    "Verifying your Google account...",
  );
  const colors = theme.palette.customColors;
  const isDark = theme.palette.mode === "dark";

  useEffect(() => {
    const executeGoogleCallback = async () => {
      const code = searchParams.get("code");

      if (!code) {
        showError("Error", "Google authorization code is missing.");
        navigate("/login", { replace: true });
        return;
      }

      if (processedGoogleCodes.has(code)) {
        return;
      }
      processedGoogleCodes.add(code);

      try {
        const result = await userStore.userAPI.googleAuthCallback(code);
        persistSession(result);

        const token = result?.authToken || result?.token;
        if (token && typeof token === "string") {
          await userStore.getUserDetails(activeWorkspace);
          navigate("/", { replace: true });
          return;
        }

        const flowStatus =
          result?.status || result?.state || result?.next_step || "pending";
        setStatusMessage(`Google auth completed with status: ${flowStatus}.`);
      } catch (_error) {
        processedGoogleCodes.delete(code);
        showError("Error", getIdentityErrorMessage(_error, "google"));
        navigate("/login", { replace: true });
      }
    };

    executeGoogleCallback();
  }, [activeWorkspace, navigate, searchParams, showError]);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1.5, sm: 2 },
        backgroundColor: isDark ? "#0F172A" : colors.pageBackground,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 360,
          minHeight: 240,
          borderRadius: "12px",
          backgroundColor: isDark ? "#1E293B" : colors.white,
          border: `1px solid ${isDark ? "#334155" : colors.lightGray}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
          py: 2.2,
        }}
      >
        <CircularProgress size={28} sx={{ color: colors.darkBlue, mb: 2 }} />
        <CustomTypography
          variant="heading4"
          color={isDark ? "#F1F5F9" : colors.darkBlueText}
          style={{ marginBottom: "0.8rem" }}
        >
          Signing you in
        </CustomTypography>
        <CustomTypography
          variant="body2"
          color={isDark ? "#94A3B8" : colors.greyTextColor}
        >
          {statusMessage}
        </CustomTypography>
      </Box>
    </Box>
  );
};

export default GoogleSuccessPopup;
